import { CommonModule, NgFor, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgApexchartsModule } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ChartComponent,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexTitleSubtitle,
  ApexXAxis,
  ApexFill,
} from 'ng-apexcharts';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { response } from 'express';
import { DashboardService } from '../../../Services/dashboard.service';
import { MasterInformationService } from '../../../Services/master-information.service';
import { apiEndPoints } from '../../../api/apiEndPoint';
import { ReportsService } from '../../../Services/reports.service';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  fill: ApexFill;
  title: ApexTitleSubtitle;
  colors: string[];
  stroke: any;
};

@Component({
  selector: 'app-sales-dashboard',
  standalone: true,
  imports: [NgFor, FormsModule, CommonModule, NgApexchartsModule, ToastrModule],
  templateUrl: './sales-dashboard.component.html',
  styleUrl: './sales-dashboard.component.css',
})
export class SalesDashboardComponent implements OnInit {
  currentYear = new Date().getFullYear();
  startYear = this.currentYear;
  years: number[] = [];
  months: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  revenueData: any;
  chartRevenueData: any;
  cityList: any;
  zoneList: any;
  selectedCityId: any;
  selectedZoneId: any;
  allRevenueDataForChart: any;
  chartSeries: any[] = [];
  selectedYear: any;
  chartFilterData: any;
  cardChartFilterData: any;
  fromDate: string = '';
  toDate: string = '';
  isToDateShow: boolean = false;
  isTableVisible: boolean = false;
  orderSummaryData: any;

  constructor(
    private dashService: DashboardService,
    private masterService: MasterInformationService,
    private toastr: ToastrService,
    private cdRef: ChangeDetectorRef,
    private reportService: ReportsService,
  ) {}

  ngOnInit() {
    this.getAllRevenueList();
    this.getCity();
    this.generateYearRange();
    this.getOrderSummary();
  }

  // ---------------------- Get Revenue List API ---------------------- //

  getAllRevenueList() {
    this.dashService
      .getRevenueData(apiEndPoints.revenueDashboardData)
      .subscribe({
        next: (response: any) => {
          this.allRevenueDataForChart = response.body.details.monthlyData;
          this.revenueData = response.body.details.summary;
          this.chartRevenueData = response.body.details;

          const dumpsterData = this.allRevenueDataForChart.map(
            (item: any) => item.sarAllocatedToDumpster
          );
          const dumpyardData = this.allRevenueDataForChart.map(
            (item: any) => item.sarAllocatedToDumpyard
          );
          const smartsortData = this.allRevenueDataForChart.map(
            (item: any) => item.sarAllocatedToSmartsort
          );

          this.chartSeries = [
            {
              name: 'SAR allocated to the dumpster',
              data: dumpsterData,
            },
            {
              name: 'SAR allocated to the dump yard',
              data: dumpyardData,
            },
            {
              name: 'SAR allocated to the smart-sort',
              data: smartsortData,
            },
          ];
        },
      });
  }

  toggleTableVisibility(event: any) {
    console.log('Chart clicked', event);
    this.isTableVisible = !this.isTableVisible; // Toggle table visibility
    this.cdRef.detectChanges();
  }

  // ------------------------------ Dumpster & Dump yard --------------------------- //

  chartOptions = {
    chart: {
      type: 'bar' as const,
      height: 350,
      stacked: false,
      toolbar: {
        show: false,
      },
      events: {
        dataPointSelection: (event: any, chartContext: any, config: any) => {
          this.toggleTableVisibility(config);
        }
      }
    },
    xaxis: {
      categories: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ],
      title: {
        text: 'Months',
      },
    },
    yaxis: {
      title: {
        text: 'Values',
      },
    },
    legend: {
      position: 'top' as const,
      horizontalAlign: 'left' as const,
    },
    colors: ['#008FFB', '#FF4560', '#7fb519'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        endingShape: 'rounded',
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
  };
  // ----------------------------------- Get All City List API ----------------------------------- //

  getCity() {
    this.masterService.getCityList(apiEndPoints.cityList).subscribe({
      next: (response: any) => {
        this.cityList = response.body.details;
      },
      error: (error) => {
        this.toastr.error(
          'An unexpected error occurred. Please try again later.',
          'Error',
          {
            timeOut: 3000,
            positionClass: 'toast-top-right',
            progressBar: true,
          }
        );
      },
    });
  }

  // ----------------------------------- City Change Dropdown ----------------------------------- //

  onCityChange(event: Event) {
    const selectedCityId = (event.target as HTMLSelectElement).value;
    if (selectedCityId === 'all') {
      this.getAllRevenueList();
    } else {
      this.selectedCityId = selectedCityId;
      this.getFilterData();
    }
    this.getZone();
  }

  // ----------------------------------- Get Zone List API ----------------------------------- //

  getZone() {
    this.dashService
      .getZoneList(apiEndPoints.zoneList, this.selectedCityId)
      .subscribe({
        next: (response: any) => {
          this.zoneList = response.body.details;
        },
        error: (error) => {
          this.toastr.error(
            'An unexpected error occurred. Please try again later.',
            'Error',
            {
              timeOut: 3000,
              positionClass: 'toast-top-right',
              progressBar: true,
            }
          );
        },
      });
  }

  // ----------------------------------- Zone Change Dropdown ----------------------------------- //

  onZoneChange(event: Event) {
    const selectedZoneId = (event.target as HTMLSelectElement).value;
    this.selectedZoneId = selectedZoneId;
    console.log(selectedZoneId)
    this.getFilterData();
  }

  generateYearRange() {
    for (let i = this.startYear; i <= this.currentYear; i++) {
      this.years.push(i);
    }
  }

  onYearChange(event: Event) {
    const selectedYear = (event.target as HTMLSelectElement).value;
    this.selectedYear = selectedYear;
    this.getFilterData();
  }

  // ----------------------------------- Filter API For Chart Revenue ----------------------------------- //

  getFilterData() {
    console.log('Fetching data with: ', {
      cityId: this.selectedCityId,
      zoneId: this.selectedZoneId,
      year: this.selectedYear,
      fromDate: this.fromDate,
      toDate: this.toDate,
    });
    this.dashService
      .getFilteredRevenueData(
        apiEndPoints.revenueDashboardData,
        this.selectedCityId,
        this.selectedZoneId,
        this.selectedYear,
        this.fromDate,
        this.toDate
      )
      .subscribe({
        next: (response: any) => {
          this.chartFilterData = response.body.details;
          this.cardChartFilterData = this.chartFilterData.summary;
        },
        error: (error) => {
          this.toastr.error(
            'An unexpected error occurred. Please try again later.',
            'Error',
            {
              timeOut: 3000,
              positionClass: 'toast-top-right',
              progressBar: true,
            }
          );
        },
      });
  }

  // ----------------------------------- Date Filter ----------------------------------- //

  onFromDateChange(event: any) {
    this.fromDate = event.target.value;
    this.isToDateShow = true;
  }

  onToDateChange(event: any) {
    this.toDate = event.target.value;
    this.getOrderSummary();
  }

    // ----------------------------------- Get Order Summary API ----------------------------------- //

  getOrderSummary() {

    const reqBody: any = {
      cityId: this.selectedCityId,
    };

    if (this.fromDate) {
      const [fromYear, fromMonth, fromDay] = this.fromDate.split("-");
      reqBody.fromDay = fromDay;
      reqBody.fromMonth = fromMonth;
      reqBody.fromYear = fromYear;
    }

    if (this.toDate) {
      const [toYear, toMonth, toDay] = this.toDate.split("-");
      reqBody.toDay = toDay;
      reqBody.toMonth = toMonth;
      reqBody.toYear = toYear;
    }

    console.log("Request Body:", reqBody);

    this.reportService
      .getOrderSummaryByDate(apiEndPoints.OrderSummaryByDate, reqBody)
      .subscribe({
        next: (response: any) => {
          console.log("API Response:", response);
          if (response.isSuccess) {
            console.log("Order Summary Data:", response.body.details);
            this.orderSummaryData = response.body.details;
            this.cdRef.detectChanges();
          } else {
            console.warn("API response indicates failure:", response);
          }
        },
        error: (error: any) => {
          console.error("API call failed:", error);
        },
      });
  }
}
